import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from './keycloak.jsx';
import { api as axiosApi, makeAuthenticatedRequest } from './api/axiosConfig.js';
import { useLanguage } from './LanguageContext.jsx';

const api = import.meta.env.VITE_API_GATEWAY;

function getFileIcon(ext) {
  const map = {
    pdf: '📄', jpg: '🖼️', jpeg: '🖼️', png: '🖼️', gif: '🖼️',
    zip: '🗜️', rar: '🗜️', tar: '🗜️', gz: '🗜️',
    mp4: '🎥', mov: '🎥', avi: '🎥',
    mp3: '🎵', wav: '🎵',
    txt: '📄', md: '📄', json: '📄',
    doc: '📄', docx: '📄', xls: '📊', xlsx: '📊'
  };
  return map[ext.toLowerCase()] || '📄';
}

function getBadgeColor(ext) {
  const map = {
    pdf: 'bg-red-500',
    jpg: 'bg-green-600', jpeg: 'bg-green-600', png: 'bg-green-600', gif: 'bg-green-600',
    zip: 'bg-yellow-500', rar: 'bg-yellow-500',
    mp4: 'bg-purple-500', mp3: 'bg-indigo-500',
    txt: 'bg-gray-500', json: 'bg-blue-500'
  };
  return map[ext.toLowerCase()] || 'bg-gray-400';
}

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function getSimMeta(fileObj) {
  const fileName = fileObj.name || fileObj.key || fileObj;
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const size = fileObj.size || 0;
  
  return {
    ext,
    size,
    isImage: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)
  };
}

function getMimeTypeFromExtension(ext) {
  const mimeMap = {
    // Images
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    // Documents
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    // Text files
    'txt': 'text/plain',
    'md': 'text/markdown',
    'html': 'text/html',
    'css': 'text/css',
    'js': 'application/javascript',
    'json': 'application/json',
    'xml': 'application/xml',
    // Archives
    'zip': 'application/zip',
    'rar': 'application/x-rar-compressed',
    'tar': 'application/x-tar',
    'gz': 'application/gzip',
    // Video
    'mp4': 'video/mp4',
    'avi': 'video/x-msvideo',
    'mov': 'video/quicktime',
    'mkv': 'video/x-matroska',
    // Audio
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'flac': 'audio/flac'
  };
  
  return mimeMap[ext?.toLowerCase()] || 'application/octet-stream';
}

export default function App() {
  const { token, keycloak } = useAuth();
  const { t } = useLanguage();
  console.log('🏗️ App component - Token status:', token ? 'AVAILABLE' : 'NOT AVAILABLE');
  console.log('🏗️ App component - Keycloak status:', keycloak?.authenticated ? 'AUTHENTICATED' : 'NOT AUTHENTICATED');
  
  // Estados para la aplicación
  const [buckets, setBuckets] = useState([]);
  const [currentBucket, setCurrentBucket] = useState('');
  const [currentPath, setCurrentPath] = useState('');
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [renameMap, setRenameMap] = useState({});
  // Estados para edición inline
  const [editingItem, setEditingItem] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  // Estados para selección múltiple
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isSelectMode, setIsSelectMode] = useState(false);
  // Estados para mejoras de UI
  const [viewMode, setViewMode] = useState('list'); // 'cards' o 'list'
  const [editingMime, setEditingMime] = useState(null);
  const [filterText, setFilterText] = useState(''); // Estado para filtro
  const [bucketFilterText, setBucketFilterText] = useState(''); // Estado para filtro de buckets
  // Estado para el token actual (para manejo de renovaciones)
  const [currentToken, setCurrentToken] = useState(token);

  const inputFileRef = useRef();

  // Actualizar token local cuando cambie el token de contexto
  useEffect(() => {
    setCurrentToken(token);
  }, [token]);

  // Helper para manejar errores de autenticación
  const handleAuthError = (error) => {
    if (error.needsReauth) {
      console.log('🔑 Se requiere reautenticación, redirigiendo a login...');
      keycloak.logout();
      return true; // Indica que se manejó como error de auth
    }
    return false; // No es error de auth, continuar con el manejo normal
  };

  // Función personalizada para requests autenticados con manejo de renovación
  const makeAuthenticatedRequestWithTokenUpdate = async (config, retries = 2) => {
    try {
      return await makeAuthenticatedRequest(config, currentToken, retries);
    } catch (error) {
      // Si axiosConfig.js renovó el token automáticamente y tenemos un nuevo token,
      // actualizar nuestro estado local
      if (keycloak?.token && keycloak.token !== currentToken) {
        console.log('🔄 Token renovado detectado, actualizando estado local...');
        setCurrentToken(keycloak.token);
      }
      
      // Propagar el error para que sea manejado por el código que llama
      throw error;
    }
  };

  // Cargar buckets disponibles
  const loadBuckets = async () => {
    setError(null);
    
    if (!api) {
      setError('🚧 API Gateway no configurado. Verifica la variable de entorno VITE_API_GATEWAY.');
      return;
    }

    // Llamar al backend para obtener buckets desde DynamoDB
    console.log('🔄 Cargando buckets desde el API');
    console.log('🔑 Token disponible:', token ? `${token.substring(0, 50)}...` : 'NO TOKEN');
    console.log('🔑 Longitud del token:', token ? token.length : 0);
    
    // Verificar si el token ha expirado
    if (keycloak?.tokenParsed) {
      const now = Math.floor(Date.now() / 1000);
      const exp = keycloak.tokenParsed.exp;
      const timeToExpiry = exp - now;
      console.log('⏰ Token expira en:', timeToExpiry, 'segundos');
      console.log('⏰ Token expirado:', timeToExpiry <= 0 ? 'SÍ' : 'NO');
      
      if (timeToExpiry <= 60) { // Si expira en menos de 1 minuto
        console.log('🔄 Token próximo a expirar, intentando refrescar...');
        keycloak.updateToken(30).then((refreshed) => {
          if (refreshed) {
            console.log('✅ Token refrescado exitosamente');
            // Reintentar con el nuevo token
            setTimeout(loadBuckets, 100);
            return;
          } else {
            console.log('ℹ️ Token aún válido, no se refrescó');
          }
        }).catch((error) => {
          const errorMsg = error?.message || error?.toString() || 'Error desconocido';
          console.error('❌ Error refrescando token:', errorMsg);
          setError(`Error refrescando token: ${errorMsg}`);
        });
      }
    }

    if (!token) {
      setError('❌ No hay token disponible para la petición. Por favor, inicia sesión nuevamente.');
      return;
    }

    // Usar endpoint del API Gateway con configuración optimizada
    const apiUrl = `${api}?action=list_buckets`;
    console.log('🔄 Cargando buckets desde:', apiUrl);

    try {
      const response = await makeAuthenticatedRequestWithTokenUpdate({
        method: 'GET',
        url: apiUrl,
        headers: { 'Content-Type': 'application/json' }
      });
      
      console.log('✅ Buckets obtenidos:', response.data);
      setBuckets(response.data.buckets || []);
    } catch (err) {
      console.error('❌ Error cargando buckets:', err);
      
      // Manejo específico para errores de reautenticación
      if (handleAuthError(err)) return;
      
      let errorMessage = 'Error desconocido al cargar buckets';
      
      if (err.message) {
        errorMessage = err.message;
      }
      
      if (err.response) {
        errorMessage = `Error ${err.response.status}: ${err.response.statusText || err.response.data?.message || 'Error del servidor'}`;
      }
      
      if (err.message && err.message.includes('CORS')) {
        errorMessage = `Error CORS: El navegador bloquea la solicitud debido a políticas de CORS. El API Gateway necesita configurar los headers CORS correctos.\n\nDetalles: ${err.message}`;
      }
      
      setError(errorMessage);
    }
  };

  // Cargar contenido de un directorio
  const loadDirectory = async (bucket = currentBucket, path = currentPath) => {
    setError(null);
    
    if (!bucket) {
      // Mostrar buckets en la raíz
      setFolders([]);
      setFiles([]);
      setBreadcrumbs([]);
      return;
    }

    if (!api) {
      setError('🚧 API Gateway no configurado. Verifica la variable de entorno VITE_API_GATEWAY.');
      return;
    }

    // Usar endpoint del API Gateway con configuración optimizada
    const prefix = path || '';
    const apiUrl = `${api}?action=list&bucket=${encodeURIComponent(bucket)}&prefix=${encodeURIComponent(prefix)}`;
    console.log('🔄 Cargando directorio desde:', apiUrl);

    try {
      const response = await makeAuthenticatedRequestWithTokenUpdate({
        method: 'GET',
        url: apiUrl,
        headers: { 'Content-Type': 'application/json' }
      });
      
      console.log('✅ Contenido del directorio:', response.data);
      const folders = response.data.folders || [];
      const files = response.data.files || [];
      
      setFolders(folders);
      setFiles(files);
      updateBreadcrumbs(bucket, path);
      
      // Detectar acceso limitado: si estamos en la raíz del bucket y no hay contenido
      // pero el usuario puede ver el bucket, podría tener acceso a carpetas específicas
      if (!path && folders.length === 0 && files.length === 0) {
        console.log('🔍 Bucket vacío en raíz, intentando detectar carpetas accesibles...');
        // En lugar de llamar detectAccessibleFolders, llamar directamente smart_folders
        try {
          const smartResponse = await makeAuthenticatedRequestWithTokenUpdate({
            url: `${api}?action=smart_folders&bucket=${encodeURIComponent(bucket)}&prefix=`,
            method: 'GET'
          });
          
          if (smartResponse.data?.folders?.length > 0) {
            setFolders(smartResponse.data.folders);
            setFiles(smartResponse.data.files || []);
          }
        } catch (error) {
          console.error('❌ Error en smart folder detection automático:', error);
        }
      }
    } catch (err) {
      console.error('❌ Error cargando directorio:', err);
      
      // Manejo específico para errores de reautenticación
      if (handleAuthError(err)) return;
      
      let errorMessage = 'Error desconocido al cargar directorio';
      
      // Verificar si la respuesta es HTML (indica problema de routing)
      if (err.response && typeof err.response.data === 'string' && err.response.data.includes('<!DOCTYPE html>')) {
        console.log('🔍 Respuesta HTML detectada - problema de routing, intentando smart folder detection...');
        try {
          const smartResponse = await makeAuthenticatedRequestWithTokenUpdate({
            method: 'GET',
            url: `${api}?action=smart_folders&bucket=${encodeURIComponent(bucket)}&prefix=${encodeURIComponent(path || '')}`,
            headers: { 'Content-Type': 'application/json' }
          });
          
          console.log('🧠 Smart folders response after HTML error:', smartResponse.data);
          
          if (smartResponse.data.folders && smartResponse.data.folders.length > 0) {
            setFolders(smartResponse.data.folders);
            setFiles(smartResponse.data.files || []);
            updateBreadcrumbs(bucket, path);
            return;
          } else if (smartResponse.data.files && smartResponse.data.files.length > 0) {
            setFolders([]);
            setFiles(smartResponse.data.files);
            updateBreadcrumbs(bucket, path);
            return;
          }
        } catch (smartError) {
          console.error('❌ Error con smart folder detection después de HTML:', smartError);
        }
      }
      
      if (err.message) {
        errorMessage = err.message;
      }
      
      if (err.response) {
        errorMessage = `Error ${err.response.status}: ${err.response.statusText || err.response.data?.message || 'Error del servidor'}`;
        
        // Ya no hay manejo especial para 403 porque ahora se maneja en el backend
      }
      
      if (err.message.includes('CORS')) {
        errorMessage = `Error CORS: El navegador bloquea la solicitud debido a políticas de CORS. El API Gateway necesita configurar los headers CORS correctos.\n\nDetalles: ${err.message}`;        }
        
        // Si llegamos aquí, smart folders no encontró nada - no mostrar error
        console.log('🔍 Smart folder detection no encontró contenido accesible');
        setFolders([]);
        setFiles([]);
        updateBreadcrumbs(bucket, path);
        return;
    }
  };

  // Actualizar breadcrumbs de navegación
  const updateBreadcrumbs = (bucket, path) => {
    const crumbs = [];
    
    if (bucket) {
      crumbs.push({ name: bucket, bucket: bucket, path: '' });
      
      if (path) {
        const pathParts = path.split('/').filter(Boolean);
        let currentPath = '';
        pathParts.forEach(part => {
          currentPath += part + '/';
          crumbs.push({ name: part, bucket: bucket, path: currentPath });
        });
      }
    }
    
    setBreadcrumbs(crumbs);
  };

  // Navegar a un bucket
  const navigateToBucket = (bucket) => {
    setCurrentBucket(bucket);
    setCurrentPath('');
    loadDirectory(bucket, '');
  };

  // Navegar a una carpeta
  const navigateToFolder = (folder) => {
    // Si es una carpeta con ruta accesible específica, usar esa ruta
    if (typeof folder === 'object' && folder.accessible_path) {
      const accessiblePath = folder.accessible_path;
      console.log('🎯 Navegando a carpeta accesible:', accessiblePath);
      setCurrentPath(accessiblePath);
      loadDirectory(currentBucket, accessiblePath);
      return;
    }
    
    // Si folder es un objeto, extraer el nombre; si es string, usarlo directamente
    const folderName = typeof folder === 'object' ? folder.name : folder;
    
    // Construir el nuevo path, asegurándonos de que termine con /
    let newPath;
    if (currentPath.endsWith('/')) {
      newPath = currentPath + folderName + '/';
    } else {
      newPath = currentPath + '/' + folderName + '/';
    }
    
    // Si currentPath está vacío, no agregar el slash inicial
    if (currentPath === '') {
      newPath = folderName + '/';
    }
    
    setCurrentPath(newPath);
    loadDirectory(currentBucket, newPath);
  };

  // Navegar usando breadcrumbs
  const navigateToBreadcrumb = (crumb) => {
    if (!crumb.bucket) {
      // Volver a la raíz (lista de buckets)
      setCurrentBucket('');
      setCurrentPath('');
      loadDirectory('', '');
    } else {
      setCurrentBucket(crumb.bucket);
      setCurrentPath(crumb.path);
      loadDirectory(crumb.bucket, crumb.path);
    }
  };

  // Cargar buckets al inicio
  useEffect(() => {
    console.log('🚀 UseEffect buckets - Token:', token ? 'AVAILABLE' : 'NOT AVAILABLE');
    console.log('🚀 UseEffect buckets - Keycloak authenticated:', keycloak?.authenticated);
    
    if (!token || !keycloak?.authenticated) {
      console.log('⏳ Esperando autenticación completa...');
      return;
    }
    
    console.log('✅ Autenticación completa - Cargando buckets');
    loadBuckets();
  }, [token, keycloak?.authenticated]);

  // Cargar contenido cuando cambie bucket o path
  useEffect(() => {
    if (!token) return;
    loadDirectory();
  }, [currentBucket, currentPath, token]);

  const handleDrop = async e => {
    e.preventDefault();
    if (!token || !currentBucket) return;
    
    // Verificar si se arrastraron archivos o carpetas usando DataTransferItems
    const items = Array.from(e.dataTransfer.items);
    const entries = items
      .map(item => item.webkitGetAsEntry?.())
      .filter(Boolean);

    // Si no hay entries (navegadores que no soportan webkitGetAsEntry), usar files tradicional
    const directFiles = Array.from(e.dataTransfer.files);
    
    const flatFiles = [];

    // Función para procesar entradas con estructura de directorios
    async function traverse(entry, path = '') {
      return new Promise(resolve => {
        if (entry.isFile) {
          entry.file(file => {
            file.fullPath = path + file.name;
            flatFiles.push(file);
            resolve();
          });
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          reader.readEntries(async entries => {
            for (const child of entries) {
              await traverse(child, path + entry.name + '/');
            }
            resolve();
          });
        } else {
          resolve();
        }
      });
    }

    setUploading(true);
    setProgress(0);

    try {
      // Si tenemos entries (carpetas/archivos con estructura), los procesamos
      if (entries.length > 0) {
        setUploadMessage(t('upload.processingElements'));
        for (const entry of entries) {
          await traverse(entry);
        }
      } 
      // Si solo tenemos archivos directos (sin estructura de carpetas)
      else if (directFiles.length > 0) {
        setUploadMessage(t('upload.processing'));
        directFiles.forEach(file => {
          file.fullPath = file.name; // Archivos directos no tienen ruta
          flatFiles.push(file);
        });
      }

      if (flatFiles.length === 0) {
        setUploadMessage(t('upload.noValidFiles'));
        setTimeout(() => {
          setUploadMessage('');
        }, 2000);
        return;
      }

      setUploadMessage(t('upload.uploading', { count: flatFiles.length }));

      for (let i = 0; i < flatFiles.length; i++) {
        const file = flatFiles[i];
        const key = currentPath + file.fullPath;
        const res = await makeAuthenticatedRequestWithTokenUpdate({
          method: 'GET',
          url: `${api}?action=upload&bucket=${encodeURIComponent(currentBucket)}&key=${encodeURIComponent(key)}&mime_type=${encodeURIComponent(file.type || 'application/octet-stream')}`,
          headers: { 'Content-Type': 'application/json' }
        });
        await fetch(res.data.upload_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' }
        });
        setProgress(Math.round(((i + 1) / flatFiles.length) * 100));
      }
      
      setUploadMessage(t('upload.completed'));
      await loadDirectory(); // Recargar el directorio actual
    } catch (err) {
      console.error('Error durante la subida:', err);
      setUploadMessage(t('upload.dragDropError', { message: err.message }));
    } finally {
      setUploading(false);
      setTimeout(() => {
        setUploadMessage('');
        setProgress(0);
      }, 3000);
    }
  }

  const downloadFile = async key => {
    try {
      const res = await makeAuthenticatedRequestWithTokenUpdate({
        method: 'GET',
        url: `${api}?action=download&bucket=${encodeURIComponent(currentBucket)}&key=${encodeURIComponent(key)}`,
        headers: { 'Content-Type': 'application/json' }
      });
      
      // Crear un enlace temporal para forzar descarga
      const link = document.createElement('a');
      link.href = res.data.download_url;
      link.download = key.split('/').pop(); // Extraer nombre del archivo
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error al descargar archivo:', error);
      alert(t('errors.downloadFile'));
    }
  };

  const uploadFile = async () => {
    if (!selectedFile || !currentBucket) return;
    const key = currentPath + selectedFile.name;
    const res = await makeAuthenticatedRequestWithTokenUpdate({
      method: 'GET',
      url: `${api}?action=upload&bucket=${encodeURIComponent(currentBucket)}&key=${encodeURIComponent(key)}&mime_type=${encodeURIComponent(selectedFile.type || 'application/octet-stream')}`,
      headers: { 'Content-Type': 'application/json' }
    });
    await fetch(res.data.upload_url, {
      method: 'PUT',
      body: selectedFile,
      headers: {
        'Content-Type': selectedFile.type || 'application/octet-stream'
      }
    });
    setSelectedFile(null);
    inputFileRef.current.value = null;
    setTimeout(() => loadDirectory(), 500);
  };

  const deleteFile = async key => {
    if (!window.confirm(t('confirm.deleteFile', { name: key })) || !currentBucket) return;
    await makeAuthenticatedRequestWithTokenUpdate({
      method: 'GET',
      url: `${api}?action=delete&bucket=${encodeURIComponent(currentBucket)}&key=${encodeURIComponent(key)}`,
      headers: { 'Content-Type': 'application/json' }
    });
    loadDirectory(); // Recargar directorio
  };

  const renameFile = async (oldKey, newKey) => {
    console.log('📄 renameFile llamado con:', { 
      oldKey, 
      newKey, 
      currentBucket,
      newKeyEmpty: !newKey,
      sameKey: newKey === oldKey,
      noBucket: !currentBucket
    });
    
    if (!newKey || newKey === oldKey || !currentBucket) {
      console.log('❌ renameFile se sale temprano por condición inicial');
      return;
    }
    
    console.log('📄 renameFile ejecutándose:', { oldKey, newKey, currentBucket });
    
    try {
      const requestConfig = {
        method: 'POST',
        url: `${api}?action=rename&bucket=${encodeURIComponent(currentBucket)}`,
        data: {
          old_key: oldKey,
          new_key: newKey
        },
        headers: { 'Content-Type': 'application/json' }
      };
      
      console.log('🌐 renameFile - Preparando request:', requestConfig);
      console.log('🔑 renameFile - Token disponible:', !!token);
      console.log('🔑 renameFile - currentToken:', !!currentToken);
      
      const response = await makeAuthenticatedRequestWithTokenUpdate(requestConfig);
      
      console.log('✅ renameFile exitoso - Response completa:', response);
      console.log('✅ renameFile exitoso - Response.data:', response?.data);
      console.log('✅ renameFile exitoso - Response.status:', response?.status);
      setRenameMap(prev => ({ ...prev, [oldKey]: '' }));
      setEditingItem(null);
      setEditingValue('');
      loadDirectory();
    } catch (error) {
      console.error('❌ Error renombrando archivo - Error completo:', error);
      console.error('❌ Error renombrando archivo - Error.message:', error.message);
      console.error('❌ Error renombrando archivo - Error.response:', error.response);
      console.error('❌ Error renombrando archivo - Error.response.data:', error.response?.data);
      let errorMessage = t('errors.renameFile');
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  // Funciones para edición inline
  const startEditing = (itemKey, currentName) => {
    setEditingItem(itemKey);
    setEditingValue(currentName);
  };

  const cancelEditing = () => {
    setEditingItem(null);
    setEditingValue('');
  };

  const renameFolder = async (oldKey, newKey) => {
    if (!newKey || newKey === oldKey || !currentBucket) return;
    
    console.log('📁 renameFolder ejecutándose:', { oldKey, newKey, currentBucket });
    
    try {
      // Para carpetas, enviar directamente al backend con la carpeta completa
      // Asegurémonos de que las keys terminen con '/'
      const oldPrefix = oldKey.endsWith('/') ? oldKey : oldKey + '/';
      const newPrefix = newKey.endsWith('/') ? newKey : newKey + '/';
      
      console.log('📁 Prefijos calculados:', { oldPrefix, newPrefix });
      console.log('📁 Enviando petición de renombrado recursivo al backend');
      
      // Enviar una sola petición al backend para renombrado recursivo
      const requestConfig = {
        method: 'POST',
        url: `${api}?action=rename&bucket=${encodeURIComponent(currentBucket)}`,
        data: {
          old_key: oldPrefix,
          new_key: newPrefix
        },
        headers: { 'Content-Type': 'application/json' }
      };
      
      console.log('📁 Configuración de petición:', requestConfig);
      
      const response = await makeAuthenticatedRequestWithTokenUpdate(requestConfig);
      
      console.log('✅ renameFolder exitoso - Response:', response);
      
      setEditingItem(null);
      setEditingValue('');
      loadDirectory(); // Recargar directorio
    } catch (error) {
      console.error('❌ Error renombrando carpeta:', error);
      let errorMessage = t('errors.renameFolder');
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  const saveEditing = () => {
    if (editingItem && editingValue && editingValue !== getItemName(editingItem)) {
      const oldPath = editingItem;
      const pathParts = oldPath.split('/');
      pathParts[pathParts.length - (oldPath.endsWith('/') ? 2 : 1)] = editingValue;
      const newPath = pathParts.join('/');
      
      // Determinar si es archivo o carpeta
      // Mejorar la lógica: si termina con '/' es carpeta, si no, es archivo
      if (oldPath.endsWith('/')) {
        // Es una carpeta (termina con '/')
        renameFolder(oldPath, newPath);
      } else {
        // Es un archivo (no termina con '/')
        renameFile(oldPath, newPath);
      }
    } else {
      cancelEditing();
    }
  };

  const getItemName = (itemKey) => {
    if (itemKey.endsWith('/')) {
      // Es una carpeta, obtener el último segmento antes del '/'
      const parts = itemKey.split('/').filter(Boolean);
      return parts[parts.length - 1];
    } else {
      // Es un archivo
      return itemKey.split('/').pop();
    }
  };

  // Funciones para selección múltiple
  const toggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    setSelectedItems(new Set());
  };

  const toggleItemSelection = (itemKey, itemType) => {
    const newSelected = new Set(selectedItems);
    const fullKey = `${itemType}:${itemKey}`;
    
    if (newSelected.has(fullKey)) {
      newSelected.delete(fullKey);
    } else {
      newSelected.add(fullKey);
    }
    setSelectedItems(newSelected);
  };

  const selectAllItems = () => {
    const allItems = new Set();
    folders.forEach(folder => allItems.add(`folder:${folder.key || folder.name}`));
    files.forEach(file => allItems.add(`file:${file.key}`));
    setSelectedItems(allItems);
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  // Eliminar carpeta (recursivamente)
  const deleteFolder = async (folderKey) => {
    if (!window.confirm(t('confirm.deleteFolder', { name: folderKey }))) return;
    
    try {
      // Usar la nueva acción delete_folder del backend
      const response = await makeAuthenticatedRequestWithTokenUpdate({
        method: 'GET',
        url: `${api}?action=delete_folder&bucket=${encodeURIComponent(currentBucket)}&prefix=${encodeURIComponent(folderKey)}`,
        headers: { 'Content-Type': 'application/json' }
      });

      console.log('Carpeta eliminada:', response.data);
      
      loadDirectory(); // Recargar directorio
    } catch (error) {
      console.error('Error eliminando carpeta:', error);
      let errorMessage = t('errors.deleteFolder');
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  // Eliminar múltiples elementos seleccionados
  const deleteSelectedItems = async () => {
    if (selectedItems.size === 0) return;
    
    if (!window.confirm(t('confirm.deleteSelected', { count: selectedItems.size }))) return;

    try {
      let deletedCount = 0;
      for (const item of selectedItems) {
        const [type, key] = item.split(':');
        if (type === 'file') {
          await makeAuthenticatedRequestWithTokenUpdate({
            method: 'GET',
            url: `${api}?action=delete&bucket=${encodeURIComponent(currentBucket)}&key=${encodeURIComponent(key)}`,
            headers: { 'Content-Type': 'application/json' }
          });
          deletedCount++;
        } else if (type === 'folder') {
          const response = await makeAuthenticatedRequestWithTokenUpdate({
            method: 'GET',
            url: `${api}?action=delete_folder&bucket=${encodeURIComponent(currentBucket)}&prefix=${encodeURIComponent(key)}`,
            headers: { 'Content-Type': 'application/json' }
          });
          deletedCount += response.data.deleted_count || 1;
        }
      }
      
      setSelectedItems(new Set());
      setIsSelectMode(false);
      loadDirectory();
    } catch (error) {
      console.error('Error eliminando elementos:', error);
      let errorMessage = t('errors.deleteItems');
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  // Descargar múltiples archivos seleccionados
  const downloadSelectedFiles = async () => {
    const fileItems = Array.from(selectedItems).filter(item => item.startsWith('file:'));
    if (fileItems.length === 0) {
      alert(t('errors.noFilesSelected'));
      return;
    }

    try {
      for (const item of fileItems) {
        const key = item.split(':')[1];
        await downloadFile(key);
        // Pequeña pausa entre descargas para evitar sobrecarga
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error('Error descargando archivos:', error);
      alert(t('errors.downloadSomeFiles'));
    }
  };

  // Crear nueva carpeta
  const createFolder = async () => {
    const folderName = prompt(t('files.folderName'));
    if (!folderName || !currentBucket) return;
    
    try {
      // Crear un archivo placeholder para crear la carpeta
      const folderKey = currentPath + folderName + '/.keep';
      const res = await makeAuthenticatedRequestWithTokenUpdate({
        method: 'GET',
        url: `${api}?action=upload&bucket=${encodeURIComponent(currentBucket)}&key=${encodeURIComponent(folderKey)}&mime_type=text/plain`,
        headers: { 'Content-Type': 'application/json' }
      });
      
      // Subir archivo placeholder
      await fetch(res.data.upload_url, {
        method: 'PUT',
        body: 'Este archivo mantiene la carpeta existente',
        headers: { 'Content-Type': 'text/plain' }
      });
      
      loadDirectory(); // Recargar directorio
    } catch (err) {
      console.error('Error creando carpeta:', err);
      alert(t('errors.createFolderFailed') + ': ' + err.message);
    }
  };

  // Funciones para edición inline de MIME types
  const startEditingMime = (fileKey, currentMime) => {
    setEditingMime({ key: fileKey, value: currentMime || 'application/octet-stream' });
  };

  const cancelEditingMime = () => {
    setEditingMime(null);
  };

  const saveEditingMime = async () => {
    if (!editingMime || !currentBucket) return;
    
    try {
      await makeAuthenticatedRequestWithTokenUpdate({
        method: 'POST',
        url: `${api}?action=update_mime&bucket=${encodeURIComponent(currentBucket)}`,
        data: {
          key: editingMime.key,
          mime_type: editingMime.value
        },
        headers: { 'Content-Type': 'application/json' }
      });
      
      setEditingMime(null);
      loadDirectory(); // Recargar para obtener los metadatos actualizados
    } catch (error) {
      console.error('Error actualizando MIME type:', error);
      alert(t('errors.mimeUpdate') + ': ' + (error.response?.data?.error || error.message));
    }
  };

  const openFile = async key => {
    try {
      const res = await makeAuthenticatedRequestWithTokenUpdate({
        method: 'GET',
        url: `${api}?action=view&bucket=${encodeURIComponent(currentBucket)}&key=${encodeURIComponent(key)}`,
        headers: { 'Content-Type': 'application/json' }
      });
      
      // Abrir en nueva ventana para que el navegador maneje el tipo MIME
      window.open(res.data.view_url, '_blank');
    } catch (error) {
      console.error('Error al abrir archivo:', error);
      alert(t('errors.openFile'));
    }
  };

  // Funciones de filtrado
  const filterItems = (items, searchText) => {
    if (!searchText.trim()) return items;
    
    const lowerSearchText = searchText.toLowerCase();
    return items.filter(item => {
      const itemName = getItemName(item.key || item.name || item);
      return itemName.toLowerCase().includes(lowerSearchText);
    });
  };

  // Aplicar filtros a archivos y carpetas
  const filteredFolders = filterItems(folders, filterText);
  const filteredFiles = filterItems(files, filterText);
  const hasFilterResults = filteredFolders.length > 0 || filteredFiles.length > 0;
  const totalFilteredItems = filteredFolders.length + filteredFiles.length;

  // Aplicar filtros a buckets
  const filteredBuckets = buckets.filter(bucket => {
    if (!bucketFilterText.trim()) return true;
    const lowerSearchText = bucketFilterText.toLowerCase();
    const bucketName = (bucket.name || '').toLowerCase();
    const bucketDisplayName = (bucket.displayName || '').toLowerCase();
    return bucketName.includes(lowerSearchText) || bucketDisplayName.includes(lowerSearchText);
  });

  // Generar enlace de proxy seguro
  const getProxyLink = (key) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/proxy?bucket=${encodeURIComponent(currentBucket)}&key=${encodeURIComponent(key)}`;
  };

  // Generar URL pública de S3
  const getPublicLink = (key) => {
    return `https://${currentBucket}.s3.amazonaws.com/${encodeURIComponent(key)}`;
  };

  // Copiar enlace al clipboard
  const copyToClipboard = async (text, successMessage) => {
    try {
      await navigator.clipboard.writeText(text);
      alert(successMessage);
    } catch (error) {
      // Fallback para navegadores más antiguos
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        alert(successMessage);
      } catch (fallbackError) {
        console.error('Error al copiar:', fallbackError);
        alert('Error al copiar al portapapeles');
      }
      document.body.removeChild(textArea);
    }
  };

  // Funciones de copia específicas
  const copyProxyLink = (key) => {
    const link = getProxyLink(key);
    copyToClipboard(link, t('actions.copyProxyLink'));
  };

  const copyPublicLink = (key) => {
    const link = getPublicLink(key);
    copyToClipboard(link, t('actions.copyPublicLink'));
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      {/* Mostrar errores si los hay */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{t('errors.loadData')}</h3>
              <div className="mt-2 text-sm text-red-700">
                <pre className="whitespace-pre-wrap">{error}</pre>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => {
                    setError(null);
                    if (!currentBucket) {
                      loadBuckets();
                    } else {
                      loadDirectory(currentBucket, currentPath);
                    }
                  }}
                  className="bg-red-100 text-red-800 px-3 py-2 rounded-md text-sm font-medium hover:bg-red-200"
                >
                  Reintentar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Breadcrumbs de navegación */}
      <nav className="flex mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <button
              onClick={() => navigateToBreadcrumb({})}
              className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600"
            >
              🏠 Buckets
            </button>
          </li>
          {breadcrumbs.map((crumb, index) => (
            <li key={index}>
              <div className="flex items-center">
                <svg className="w-3 h-3 text-gray-400 mx-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/>
                </svg>
                <button
                  onClick={() => navigateToBreadcrumb(crumb)}
                  className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
                >
                  {crumb.name}
                </button>
              </div>
            </li>
          ))}
        </ol>
      </nav>

      {/* Título de la sección actual */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {!currentBucket ? t('buckets.title') : 
           !currentPath ? `Bucket: ${currentBucket}` : 
           `${currentBucket}/${currentPath}`}
        </h1>
        {currentBucket && (
          <p className="text-sm text-gray-600 mt-1">
            {t('navigation.exploreFiles')}
          </p>
        )}
      </div>

      {/* Lista de buckets (solo si no hay bucket seleccionado) */}
      {!currentBucket && (
        <div>
          {/* Campo de filtro para buckets */}
          {buckets.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder={t('filter.bucketPlaceholder')}
                    value={bucketFilterText}
                    onChange={(e) => setBucketFilterText(e.target.value)}
                    className="w-full px-3 py-2 pl-10 pr-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
                {bucketFilterText && (
                  <button
                    onClick={() => setBucketFilterText('')}
                    className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    {t('filter.clear')}
                  </button>
                )}
              </div>
              {bucketFilterText && (
                <div className="mt-2 text-sm text-gray-600">
                  {t('filter.showingResults', { 
                    count: filteredBuckets.length, 
                    total: buckets.length 
                  })}
                </div>
              )}
            </div>
          )}
          
          {filteredBuckets.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {filteredBuckets.map(bucket => (
                <button
                key={bucket.name}
                onClick={() => navigateToBucket(bucket.name)}
                className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                >
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">🪣</span>
                    <div>
                      <h3 className="font-medium text-gray-900">{bucket.displayName || bucket.name}</h3>
                      <p className="text-sm text-gray-500">{bucket.description || 'Bucket S3'}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : buckets.length > 0 && bucketFilterText ? (
            <div className="text-center py-12">
              <span className="text-6xl mb-4 block">🔍</span>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('filter.noResults')}</h3>
              <p className="text-gray-500 mb-4">
                {t('filter.noBucketsFound', { searchText: bucketFilterText })}
              </p>
              <button
                onClick={() => setBucketFilterText('')}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                {t('filter.clear')}
              </button>
            </div>
          ) : buckets.length === 0 && !error && (
            <div className="text-center py-12">
              <span className="text-6xl mb-4 block">🪣</span>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('loading.buckets')}</h3>
              <p className="text-gray-500">
                Obteniendo lista de buckets disponibles desde el API.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Área de subida simplificada (solo si hay bucket seleccionado) */}
      {currentBucket && (
        <div
        onDrop={handleDrop}
        onDragOver={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDragEnter={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        className="border-2 border-dashed border-blue-400 bg-blue-50 p-6 rounded text-center text-blue-700 font-semibold mb-4 hover:bg-blue-100 transition-colors relative"
        >
          {uploading ? (
            <div>
              <div>{uploadMessage}</div>
              <div className="w-full bg-blue-100 rounded h-2 mt-2">
                <div
                  className="bg-blue-500 h-2 rounded transition-all"
                  style={{ width: `${progress}%` }}
                  ></div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-4xl">📁</div>
              <div>{t('upload.dragdropEnhanced')}</div>
              <div className="text-sm text-blue-600 space-y-2">
                <div className="flex justify-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById('file-input').click();
                    }}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
                    >
                    📄 {t('actions.selectFiles')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const folderInput = document.getElementById('folder-input');
                      if (folderInput && 'webkitdirectory' in folderInput) {
                        folderInput.click();
                      } else {
                        alert(t('upload.folderNotSupported'));
                      }
                    }}
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
                    title={t('upload.folderHint')}
                    >
                    📁 {t('actions.selectFolder')}
                  </button>
                </div>
              </div>
              {/* Input para archivos individuales */}
              <input
                id="file-input"
                type="file"
                multiple
                accept="*/*"
                className="hidden"
                onChange={async e => {
                  if (!token) return;
                  const flatFiles = Array.from(e.target.files);
                  setUploading(true);
                  setProgress(0);
                  setUploadMessage(t('upload.uploading', { count: flatFiles.length }));
                  try {
                    for (let i = 0; i < flatFiles.length; i++) {
                      const file = flatFiles[i];
                      const key = currentPath + file.name;
                      const res = await makeAuthenticatedRequestWithTokenUpdate({
                        method: 'GET',
                        url: `${api}?action=upload&bucket=${encodeURIComponent(currentBucket)}&key=${encodeURIComponent(key)}&mime_type=${encodeURIComponent(file.type || 'application/octet-stream')}`,
                        headers: { 'Content-Type': 'application/json' }
                      });
                      await fetch(res.data.upload_url, {
                        method: 'PUT',
                        body: file,
                        headers: { 'Content-Type': file.type || 'application/octet-stream' }
                      });
                      setProgress(Math.round(((i + 1) / flatFiles.length) * 100));
                    }
                    setUploadMessage(t('upload.completed'));
                    await loadDirectory();
                  } catch (err) {
                    console.error('Error:', err);
                    setUploadMessage(t('upload.error', { message: err.message }));
                  } finally {
                    setUploading(false);
                    setTimeout(() => {
                      setUploadMessage('');
                      setProgress(0);
                      e.target.value = ''; // Limpiar input
                    }, 3000);
                  }
                }}
              />
              {/* Input para carpetas */}
              <input
                id="folder-input"
                type="file"
                webkitdirectory="true"
                directory="true"
                mozdirectory="true"
                className="hidden"
                onChange={async e => {
                  if (!token) return;
                  const flatFiles = Array.from(e.target.files);
                  setUploading(true);
                  setProgress(0);
                  setUploadMessage(t('upload.uploading', { count: flatFiles.length }));
                  try {
                    for (let i = 0; i < flatFiles.length; i++) {
                      const file = flatFiles[i];
                      const key = currentPath + file.webkitRelativePath;
                      const res = await makeAuthenticatedRequestWithTokenUpdate({
                        method: 'GET',
                        url: `${api}?action=upload&bucket=${encodeURIComponent(currentBucket)}&key=${encodeURIComponent(key)}&mime_type=${encodeURIComponent(file.type || 'application/octet-stream')}`,
                        headers: { 'Content-Type': 'application/json' }
                      });
                      await fetch(res.data.upload_url, {
                        method: 'PUT',
                        body: file,
                        headers: { 'Content-Type': file.type || 'application/octet-stream' }
                      });
                      setProgress(Math.round(((i + 1) / flatFiles.length) * 100));
                    }
                    setUploadMessage(t('upload.completed'));
                    await loadDirectory();
                  } catch (err) {
                    console.error('Error:', err);
                    setUploadMessage(t('upload.error', { message: err.message }));
                  } finally {
                    setUploading(false);
                    setTimeout(() => {
                      setUploadMessage('');
                      setProgress(0);
                      e.target.value = ''; // Limpiar input
                    }, 3000);
                  }
                }}
                />
            </div>
          )}
        </div>
      )}

      {/* Navegación de carpetas y archivos */}
      {currentBucket && (
        <div className="space-y-4">
          {/* Botón "Volver" si no estamos en la raíz del bucket */}
          {currentPath && (
            <div className="pb-2 border-b">
              <button
                onClick={() => {
                  const parentPath = currentPath.split('/').slice(0, -2).join('/');
                  const newPath = parentPath ? parentPath + '/' : '';
                  setCurrentPath(newPath);
                  loadDirectory(currentBucket, newPath);
                }}
                className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                >
                <span className="mr-2">⬆️</span>
                {t('navigation.back')}
              </button>
            </div>
          )}

          {/* Barra de herramientas para selección múltiple */}
          <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center space-x-3">
              <button
                onClick={toggleSelectMode}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  isSelectMode 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                >
                {isSelectMode ? t('selection.cancel') : t('selection.enable')}
              </button>
              
              {isSelectMode && (
                <>
                  <button
                    onClick={selectAllItems}
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm"
                    >
                    {t('selection.selectAll')}
                  </button>
                  <button
                    onClick={clearSelection}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm"
                    >
                    {t('selection.clearSelection')}
                  </button>
                </>
              )}
            </div>

            {/* Acciones para elementos seleccionados */}
            {isSelectMode && selectedItems.size > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {selectedItems.size} elemento(s) seleccionado(s)
                </span>
                <button
                  onClick={downloadSelectedFiles}
                  className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                  >
                  📥 {t('actions.download')}
                </button>
                <button
                  onClick={deleteSelectedItems}
                  className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                  >
                  🗑️ {t('actions.delete')}
                </button>
              </div>
            )}
          </div>

          {/* Controles de vista */}
          <div className="flex justify-end items-center space-x-2">
            <span className="text-sm text-gray-600">{t('navigation.viewLabel')}:</span>
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-2 rounded-md text-sm transition-colors ${
                viewMode === 'cards' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              >
              🔲 {t('view.cards')}
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 rounded-md text-sm transition-colors ${
                viewMode === 'list' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              >
              📄 {t('view.list')}
            </button>
          </div>

          {/* Botones de Nueva carpeta y Refrescar */}
          <div className="flex justify-end items-center space-x-2">
            <button
              onClick={createFolder}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
              >
              📁 {t('actions.createFolder')}
            </button>
            
            <button
              onClick={() => loadDirectory()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
              title="Refrescar listado"
              >
              🔄 {t('actions.refresh')}
            </button>
          </div>

          {/* Campo de filtro cuando hay archivos */}
          {currentBucket && (folders.length > 0 || files.length > 0) && (
            <div className="mb-4">
              <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder={t('filter.placeholder')}
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    className="w-full px-3 py-2 pl-10 pr-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
                {filterText && (
                  <button
                    onClick={() => setFilterText('')}
                    className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    {t('filter.clear')}
                  </button>
                )}
              </div>
              {filterText && (
                <p className="text-xs text-gray-500 mt-1">
                  {totalFilteredItems} {t('filter.results')}
                </p>
              )}
            </div>
          )}

          {/* Lista de carpetas */}
          {folders.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold text-gray-700 flex items-center">
                <span className="mr-2">📁</span>
                {t('navigation.foldersBreadcrumb')} ({folders.length})
              </h3>
              
              {viewMode === 'cards' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {filteredFolders.map(folder => {
                    const folderKey = folder.key || folder.name;
                    const isSelected = selectedItems.has(`folder:${folderKey}`);
                    
                    return (
                      <div key={folderKey} className="relative group">
                        <div className={`flex items-center p-3 border rounded transition-colors ${
                          isSelected ? 'bg-blue-100 border-blue-300' : 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100'
                        }`}>
                          {/* Checkbox para selección múltiple */}
                          {isSelectMode && (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleItemSelection(folderKey, 'folder')}
                              className="mr-3 h-4 w-4 text-blue-600"
                            />
                          )}
                          
                          {/* Contenido de la carpeta */}
                          <button
                            onClick={() => !isSelectMode && navigateToFolder(folder)}
                            className="flex items-center flex-1 text-left"
                            disabled={isSelectMode}
                          >
                            <span className="text-xl mr-3">📁</span>
                            {editingItem === folderKey ? (
                              <div className="flex items-center gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="text"
                                  value={editingValue}
                                  onChange={(e) => setEditingValue(e.target.value)}
                                  className="font-medium text-gray-800 border border-blue-500 rounded px-2 py-1 text-sm flex-1"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      saveEditing();
                                    } else if (e.key === 'Escape') {
                                      cancelEditing();
                                    }
                                  }}
                                />
                                <button
                                  onClick={saveEditing}
                                  className="text-green-600 hover:bg-green-100 p-1 rounded text-xs"
                                  title="Guardar"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={cancelEditing}
                                  className="text-red-600 hover:bg-red-100 p-1 rounded text-xs"
                                  title="Cancelar"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <span className="font-medium text-gray-800">{folder.name || folder}</span>
                            )}
                          </button>

                          {/* Menú contextual */}
                          {!isSelectMode && (
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditing(folderKey, folder.name || folder);
                                }}
                                className="p-1 text-blue-600 hover:bg-blue-100 rounded mr-1"
                                title="Renombrar carpeta"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteFolder(folderKey);
                                }}
                                className="p-1 text-red-600 hover:bg-red-100 rounded"
                                title={t('actions.deleteFolder')}
                              >
                                🗑️
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Vista de lista para carpetas */
                <div className="bg-white rounded-lg border overflow-hidden">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        {isSelectMode && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                            <input
                              type="checkbox"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  folders.forEach(folder => {
                                    const folderKey = folder.key || folder.name;
                                    toggleItemSelection(folderKey, 'folder');
                                  });
                                } else {
                                  setSelectedItems(new Set(Array.from(selectedItems).filter(item => !item.startsWith('folder:'))));
                                }
                              }}
                              className="h-4 w-4 text-blue-600"
                            />
                          </th>
                        )}
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('files.name')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('files.type')}
                        </th>
                        {!isSelectMode && (
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('files.actions')}
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredFolders.map(folder => {
                        const folderKey = folder.key || folder.name;
                        const isSelected = selectedItems.has(`folder:${folderKey}`);
                        
                        return (
                          <tr key={folderKey} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                            {isSelectMode && (
                              <td className="px-4 py-4 text-sm">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleItemSelection(folderKey, 'folder')}
                                  className="h-4 w-4 text-blue-600"
                                />
                              </td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <span className="text-xl mr-3">📁</span>
                                {editingItem === folderKey ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={editingValue}
                                      onChange={(e) => setEditingValue(e.target.value)}
                                      className="font-medium text-gray-900 border border-blue-500 rounded px-2 py-1 text-sm"
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          saveEditing();
                                        } else if (e.key === 'Escape') {
                                          cancelEditing();
                                        }
                                      }}
                                    />
                                    <button
                                      onClick={saveEditing}
                                      className="text-green-600 hover:bg-green-100 p-1 rounded text-xs"
                                      title="Guardar"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      onClick={cancelEditing}
                                      className="text-red-600 hover:bg-red-100 p-1 rounded text-xs"
                                      title="Cancelar"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => !isSelectMode && navigateToFolder(folder)}
                                    className="font-medium text-gray-900 hover:text-blue-600 text-left"
                                    disabled={isSelectMode}
                                  >
                                    {folder.name || folder}
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                Carpeta
                              </span>
                            </td>
                            {!isSelectMode && (
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => startEditing(folderKey, folder.name || folder)}
                                  className="text-blue-600 hover:text-blue-900 mr-3"
                                  title="Renombrar"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => deleteFolder(folderKey)}
                                  className="text-red-600 hover:text-red-900"
                                  title={t('actions.delete')}
                                >
                                  🗑️
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Lista de archivos */}
          {files.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold text-gray-700 flex items-center">
                <span className="mr-2">📄</span>
                {t('navigation.filesBreadcrumb')} ({files.length})
              </h3>
              
              {viewMode === 'cards' ? (
                <div className="space-y-3">
                  {filteredFiles.map(file => {
                    const meta = getSimMeta(file);
                    const fileName = file.name;
                    const isSelected = selectedItems.has(`file:${file.key}`);
                    
                    return (
                      <div key={file.key} className={`border p-4 rounded-lg shadow-sm transition-colors ${
                        isSelected ? 'bg-blue-50 border-blue-300' : 'bg-white'
                      }`}>
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            {/* Checkbox para selección múltiple */}
                            {isSelectMode && (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleItemSelection(file.key, 'file')}
                                className="mr-2 h-4 w-4 text-blue-600 flex-shrink-0"
                              />
                            )}
                            
                            {meta.isImage ? (
                              <img
                                src={`https://placehold.co/40?text=IMG`}
                                alt={fileName}
                                className="w-10 h-10 object-cover rounded shadow flex-shrink-0"
                              />
                            ) : (
                              <span className="text-2xl flex-shrink-0">{getFileIcon(meta.ext)}</span>
                            )}
                            <div className="min-w-0 flex-1">
                              {editingItem === file.key ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={editingValue}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    className="font-medium text-gray-900 border border-blue-500 rounded px-2 py-1 text-sm"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        saveEditing();
                                      } else if (e.key === 'Escape') {
                                        cancelEditing();
                                      }
                                    }}
                                  />
                                  <button
                                    onClick={saveEditing}
                                    className="text-green-600 hover:bg-green-100 p-1 rounded text-xs"
                                    title="Guardar"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    onClick={cancelEditing}
                                    className="text-red-600 hover:bg-red-100 p-1 rounded text-xs"
                                    title="Cancelar"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => openFile(file.key)}
                                  className="font-medium text-gray-900 truncate hover:text-blue-600 text-left w-full cursor-pointer"
                                  title="Click para abrir archivo"
                                >
                                  {fileName}
                                </button>
                              )}
                              <p className="text-sm text-gray-500">
                                {formatFileSize(file.size)} • {meta.ext?.toUpperCase() || 'FILE'}
                              </p>
                              {file.lastModified && (
                                <p className="text-xs text-gray-400">
                                  {t('files.modified')}: {new Date(file.lastModified).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Botones de acción solo cuando no está en modo selección */}
                          {!isSelectMode && (
                            <div className="flex items-center space-x-2 flex-shrink-0">
                              {editingMime && editingMime.key === file.key ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={editingMime.value}
                                    onChange={(e) => setEditingMime({...editingMime, value: e.target.value})}
                                    className="text-xs text-white px-2 py-1 rounded border border-blue-500 bg-gray-800"
                                    style={{ minWidth: '120px' }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        saveEditingMime();
                                      } else if (e.key === 'Escape') {
                                        cancelEditingMime();
                                      }
                                    }}
                                    autoFocus
                                  />
                                  <button
                                    onClick={saveEditingMime}
                                    className="text-green-600 hover:bg-green-100 p-1 rounded text-xs"
                                    title="Guardar MIME"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    onClick={cancelEditingMime}
                                    className="text-red-600 hover:bg-red-100 p-1 rounded text-xs"
                                    title="Cancelar"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => startEditingMime(file.key, file.contentType || getMimeTypeFromExtension(meta.ext))}
                                  className={`text-xs text-white px-2 py-1 rounded ${getBadgeColor(meta.ext)} hover:opacity-80`}
                                  title="Click para editar MIME type"
                                >
                                  {file.contentType || getMimeTypeFromExtension(meta.ext)}
                                </button>
                              )}
                              <button
                                onClick={() => downloadFile(file.key)}
                                className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                              >
                                {t('actions.download')}
                              </button>
                              <button
                                onClick={() => copyProxyLink(file.key)}
                                className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                              >
                                🔗 {t('actions.proxyLink')}
                              </button>
                              <button
                                onClick={() => copyPublicLink(file.key)}
                                className="bg-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-orange-700"
                              >
                                🌐 {t('actions.publicLink')}
                              </button>
                              <button
                                onClick={() => startEditing(file.key, fileName)}
                                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                              >
                                Renombrar
                              </button>
                              <button
                                onClick={() => deleteFile(file.key)}
                                className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600"
                              >
                                {t('actions.delete')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Vista de lista para archivos */
                <div className="bg-white rounded-lg border overflow-hidden">
                  {/* Vista de tabla para desktop */}
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="min-w-full table-fixed">
                      <thead className="bg-gray-50">
                        <tr>
                          {isSelectMode && (
                            <th className="w-12 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              <input
                                type="checkbox"
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    files.forEach(file => {
                                      toggleItemSelection(file.key, 'file');
                                    });
                                  } else {
                                    setSelectedItems(new Set(Array.from(selectedItems).filter(item => !item.startsWith('file:'))));
                                  }
                                }}
                                className="h-4 w-4 text-blue-600"
                              />
                            </th>
                          )}
                          <th className="w-2/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('files.name')}
                          </th>
                          <th className="w-24 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('files.size')}
                          </th>
                          <th className="w-32 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('files.type')}
                          </th>
                          <th className="w-32 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('files.modified')}
                          </th>
                          {!isSelectMode && (
                            <th className="w-32 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('files.actions')}
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                      {filteredFiles.map(file => {
                        const meta = getSimMeta(file);
                        const fileName = file.name;
                        const isSelected = selectedItems.has(`file:${file.key}`);
                        
                        return (
                          <tr key={file.key} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                            {isSelectMode && (
                              <td className="px-4 py-4 text-sm">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleItemSelection(file.key, 'file')}
                                  className="h-4 w-4 text-blue-600"
                                />
                             
                              </td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {meta.isImage ? (
                                  <img
                                    src={`https://placehold.co/32?text=IMG`}
                                    alt={fileName}
                                    className="w-8 h-8 object-cover rounded shadow mr-3"
                                  />
                                ) : (
                                  <span className="text-lg mr-3">{getFileIcon(meta.ext)}</span>
                                )}
                                {editingItem === file.key ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={editingValue}
                                      onChange={(e) => setEditingValue(e.target.value)}
                                      className="font-medium text-gray-900 border border-blue-500 rounded px-2 py-1 text-sm"
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          saveEditing();
                                        } else if (e.key === 'Escape') {
                                          cancelEditing();
                                        }
                                      }}
                                    />
                                    <button
                                      onClick={saveEditing}
                                      className="text-green-600 hover:bg-green-100 p-1 rounded text-xs"
                                      title="Guardar"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      onClick={cancelEditing}
                                      className="text-red-600 hover:bg-red-100 p-1 rounded text-xs"
                                      title="Cancelar"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => openFile(file.key)}
                                    className="font-medium text-gray-900 hover:text-blue-600 text-left cursor-pointer"
                                    title="Click para abrir archivo"
                                  >
                                    {fileName}
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatFileSize(file.size)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {editingMime && editingMime.key === file.key ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={editingMime.value}
                                    onChange={(e) => setEditingMime({...editingMime, value: e.target.value})}
                                    className="text-xs px-2 py-1 rounded border border-blue-500"
                                    style={{ minWidth: '120px' }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        saveEditingMime();
                                      } else if (e.key === 'Escape') {
                                        cancelEditingMime();
                                      }
                                    }}
                                    autoFocus
                                  />
                                  <button
                                    onClick={saveEditingMime}
                                    className="text-green-600 hover:bg-green-100 p-1 rounded text-xs"
                                    title="Guardar MIME"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    onClick={cancelEditingMime}
                                    className="text-red-600 hover:bg-red-100 p-1 rounded text-xs"
                                    title="Cancelar"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => startEditingMime(file.key, file.contentType || getMimeTypeFromExtension(meta.ext))}
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getBadgeColor(meta.ext)} text-white hover:opacity-80`}
                                  title="Click para editar MIME type"
                                >
                                  {file.contentType || getMimeTypeFromExtension(meta.ext)}
                                </button>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {file.lastModified ? new Date(file.lastModified).toLocaleDateString() : '-'}
                            </td>
                            {!isSelectMode && (
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => downloadFile(file.key)}
                                  className="text-blue-600 hover:text-blue-900 mr-2"
                                  title={t('actions.download')}
                                >
                                  📥
                                </button>
                                <button
                                  onClick={() => copyProxyLink(file.key)}
                                  className="text-purple-600 hover:text-purple-900 mr-2"
                                  title={t('actions.copyProxyLink')}
                                >
                                  🔗
                                </button>
                                <button
                                  onClick={() => copyPublicLink(file.key)}
                                  className="text-orange-600 hover:text-orange-900 mr-2"
                                  title={t('actions.copyPublicLink')}
                                >
                                  🌐
                                </button>
                                <button
                                  onClick={() => startEditing(file.key, fileName)}
                                  className="text-green-600 hover:text-green-900 mr-2"
                                  title="Renombrar"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => deleteFile(file.key)}
                                  className="text-red-600 hover:text-red-900"
                                  title={t('actions.delete')}
                                >
                                  🗑️
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Vista móvil para modo lista */}
                <div className="lg:hidden">
                  {filteredFiles.map(file => {
                    const meta = getSimMeta(file);
                    const fileName = file.name;
                    const isSelected = selectedItems.has(`file:${file.key}`);
                    
                    return (
                      <div key={file.key} className={`border-b border-gray-200 p-4 ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            {isSelectMode && (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleItemSelection(file.key, 'file')}
                                className="h-4 w-4 text-blue-600 flex-shrink-0"
                              />
                            )}
                            
                            {meta.isImage ? (
                              <img
                                src={`https://placehold.co/32?text=IMG`}
                                alt={fileName}
                                className="w-8 h-8 object-cover rounded shadow flex-shrink-0"
                              />
                            ) : (
                              <span className="text-lg flex-shrink-0">{getFileIcon(meta.ext)}</span>
                            )}
                            
                            <div className="flex-1 min-w-0">
                              {editingItem === file.key ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={editingValue}
                                    onChange={(e) => setEditingValue(e.target.value)}
                                    className="font-medium text-gray-900 border border-blue-500 rounded px-2 py-1 text-sm w-full"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        saveEditing();
                                      } else if (e.key === 'Escape') {
                                        cancelEditing();
                                      }
                                    }}
                                  />
                                  <button
                                    onClick={saveEditing}
                                    className="text-green-600 hover:bg-green-100 p-1 rounded text-xs flex-shrink-0"
                                    title="Guardar"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    onClick={cancelEditing}
                                    className="text-red-600 hover:bg-red-100 p-1 rounded text-xs flex-shrink-0"
                                    title="Cancelar"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => openFile(file.key)}
                                  className="font-medium text-gray-900 hover:text-blue-600 text-left cursor-pointer truncate block w-full"
                                  title="Click para abrir archivo"
                                >
                                  {fileName}
                                </button>
                              )}
                              
                              <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                                <span>{formatFileSize(file.size)}</span>
                                
                                {editingMime && editingMime.key === file.key ? (
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="text"
                                      value={editingMime.value}
                                      onChange={(e) => setEditingMime({...editingMime, value: e.target.value})}
                                      className="text-xs px-1 py-0.5 rounded border border-blue-500"
                                      style={{ minWidth: '80px' }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          saveEditingMime();
                                        } else if (e.key === 'Escape') {
                                          cancelEditingMime();
                                        }
                                      }}
                                      autoFocus
                                    />
                                    <button
                                      onClick={saveEditingMime}
                                      className="text-green-600 hover:bg-green-100 p-0.5 rounded text-xs"
                                      title="Guardar MIME"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      onClick={cancelEditingMime}
                                      className="text-red-600 hover:bg-red-100 p-0.5 rounded text-xs"
                                      title="Cancelar"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => startEditingMime(file.key, file.contentType || getMimeTypeFromExtension(meta.ext))}
                                    className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${getBadgeColor(meta.ext)} text-white hover:opacity-80`}
                                    title="Click para editar MIME type"
                                  >
                                    {file.contentType || getMimeTypeFromExtension(meta.ext)}
                                  </button>
                                )}
                                
                                <span>{file.lastModified ? new Date(file.lastModified).toLocaleDateString() : '-'}</span>
                              </div>
                            </div>
                          </div>
                          
                          {!isSelectMode && (
                            <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                              <button
                                onClick={() => downloadFile(file.key)}
                                className="text-blue-600 hover:text-blue-900 p-1"
                                title={t('actions.download')}
                              >
                                📥
                              </button>
                              <button
                                onClick={() => copyProxyLink(file.key)}
                                className="text-purple-600 hover:text-purple-900 p-1"
                                title={t('actions.copyProxyLink')}
                              >
                                🔗
                              </button>
                              <button
                                onClick={() => copyPublicLink(file.key)}
                                className="text-orange-600 hover:text-orange-900 p-1"
                                title={t('actions.copyPublicLink')}
                              >
                                🌐
                              </button>
                              <button
                                onClick={() => startEditing(file.key, fileName)}
                                className="text-green-600 hover:text-green-900 p-1"
                                title="Renombrar"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => deleteFile(file.key)}
                                className="text-red-600 hover:text-red-900 p-1"
                                title={t('actions.delete')}
                              >
                                🗑️
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
            </div>
          )}
        </div>
      )}

          {/* Mensaje cuando la carpeta está vacía o no hay resultados del filtro */}
          {!filterText && folders.length === 0 && files.length === 0 && (
            <div className="text-center py-12">
              <span className="text-6xl mb-4 block">📁</span>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('files.empty')}</h3>
              <p className="text-gray-500">
                {t('upload.dragdrop')}
              </p>
            </div>
          )}
          
          {/* Mensaje cuando el filtro no encuentra resultados */}
          {filterText && filteredFolders.length === 0 && filteredFiles.length === 0 && (
            <div className="text-center py-12">
              <span className="text-6xl mb-4 block">🔍</span>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron resultados</h3>
              <p className="text-gray-500">
                No hay archivos o carpetas que coincidan con "{filterText}"
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
